import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import React, { useEffect, useState } from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  getWorkflowHistoryState,
  IWorkflowHistoryState,
  requestWorkflowHistory,
  subscribeWorkflowHistoryState
} from "../../flows/process-editor/workflow-history-bus";

/**
 * Undo/redo for the process diagram.
 *
 * The screen designer's `UndoRedo` cannot stand in for this one: it drives `sd.undo()`, which
 * rewinds the IScreenDesign tree, and a workflow never enters that store. Pointed at a process
 * it would quietly step the last loaded SCREEN's history instead — which is exactly what the
 * old logo/avatar menu did on this dashboard until KOA-16942 removed it. These buttons talk to
 * the canvas that owns the real history (bpmn-js' command stack, or the per-user inverse-op
 * stack while others are editing) through `workflow-history-bus`.
 */
export const WorkflowUndoRedo = (): React.ReactElement => {
  const [history, setHistory] = useState<IWorkflowHistoryState>(getWorkflowHistoryState);

  useEffect((): (() => void) => subscribeWorkflowHistoryState(setHistory), []);

  const ctrlKey: string = CommonHelperFunctions.isMac() ? "⌘" : "Ctrl+";

  return (
    <div className="kuika-flex-center">
      <KuikaTooltip position="bottom" content={`Undo (${ctrlKey}Z)`}>
        {/* The tooltip needs a live element to hang off: a disabled button fires no pointer
            events of its own, so the trigger has to be the wrapper. */}
        <span className="kuika-subbar__history-trigger">
          <KuikaButton
            data-testid="subBarWorkflowUndoButton"
            aria-label="Undo"
            onClick={(): void => requestWorkflowHistory("undo")}
            disabled={!history.canUndo}
            isIcon
            type="tertiary"
            size="sm"
            color="neutral"
          >
            <KuikaSvg width={16} height={16} name="arrow-back-up" type="arrows" disabled={!history.canUndo} />
          </KuikaButton>
        </span>
      </KuikaTooltip>
      <KuikaTooltip position="bottom" content={`Redo (${ctrlKey}Y)`}>
        <span className="kuika-subbar__history-trigger">
          <KuikaButton
            data-testid="subBarWorkflowRedoButton"
            aria-label="Redo"
            onClick={(): void => requestWorkflowHistory("redo")}
            disabled={!history.canRedo}
            isIcon
            type="tertiary"
            size="sm"
            color="neutral"
          >
            <KuikaSvg width={16} height={16} name="arrow-forward-up" type="arrows" disabled={!history.canRedo} />
          </KuikaButton>
        </span>
      </KuikaTooltip>
    </div>
  );
};
